import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroAnime, formatViews } from "@/mocks/anime";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStart = useRef<number | null>(null);
  const total = heroAnime.length;

  const go = (next: number) => setIndex((next + total) % total);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [total]);

  const current = heroAnime[index];

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      aria-label="热门动漫焦点轮播"
      onTouchStart={(e) => {
        touchStart.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchStart.current === null) return;
        const diff = e.changedTouches[0].clientX - touchStart.current;
        if (Math.abs(diff) > 50) go(diff < 0 ? index + 1 : index - 1);
        touchStart.current = null;
      }}
    >
      <div className="relative w-full h-[480px] md:h-[560px] lg:h-[600px]">
        {heroAnime.map((anime, i) => (
          <div
            key={anime.id}
            className={`absolute inset-0 transition-opacity duration-700 ${i === index ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          >
            <img
              src={anime.cover.replace("width=600&height=840", "width=1600&height=900")}
              alt={`${anime.title}焦点海报`}
              title={`${anime.title} - 妖精动漫热门推荐`}
              className="w-full h-full object-cover object-top"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/75 to-primary-950/15" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/85 via-transparent to-primary-950/25" />

        <div className="absolute inset-0">
          <div className="w-full h-full px-4 md:px-6 flex items-center">
            <div className="w-full max-w-[640px] pb-10">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-2.5 py-1 rounded-md bg-accent-500 text-primary-950 text-[11px] font-bold tracking-wide">
                  本周焦点
                </span>
                <span className="text-[12px] text-background-200">
                  {current.type} · {current.region} · {current.year}
                </span>
              </div>
              <h2 className="text-3xl md:text-5xl font-black text-background-50 tracking-tight leading-tight">
                {current.title}
              </h2>
              <p className="mt-1.5 text-[11px] md:text-xs tracking-[0.22em] text-accent-300 font-semibold uppercase">
                {current.originalTitle}
              </p>
              <p className="mt-4 text-[13px] md:text-[15px] leading-7 text-background-200 max-w-[540px] line-clamp-2 md:line-clamp-3">
                {current.summary}
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-background-200">
                <span className="flex items-center gap-1 font-semibold text-accent-400">
                  <i className="ri-star-fill text-[11px]" /> {current.rating.toFixed(1)} 分
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-play-circle-line" /> {formatViews(current.views)} 播放
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line" /> {current.serialDay}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-list-check" />
                  {current.status === "已完结" ? `全 ${current.episodes} 话` : `更新至第 ${current.chapterList.length + 6} 话`}
                </span>
              </div>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${current.id}`}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 hover:bg-accent-600 text-primary-950 text-[14px] font-bold transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-fill text-lg leading-none" />
                  查看作品详情
                </Link>
                <Link
                  to="/category/guoman"
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-background-100/40 text-background-50 text-[14px] font-medium hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-grid-line" />
                  浏览全部作品
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="hidden lg:block absolute bottom-7 right-6 w-[300px]">
          <div className="bg-background-50/10 backdrop-blur-sm border border-background-50/15 rounded-lg p-3">
            <p className="text-[11px] font-semibold tracking-widest text-accent-300 mb-2">热点速览</p>
            {heroAnime.slice(0, 3).map((item, i) => (
              <Link key={item.id} to={`/detail/${item.id}`} className="flex items-center gap-2 py-1.5 group cursor-pointer">
                <span className="text-[12px] font-black text-background-50/50 w-4">{i + 1}</span>
                <span className="flex-1 text-[12px] text-background-100 line-clamp-1 group-hover:text-accent-300 transition-colors">
                  {item.title}
                </span>
                <i className="ri-arrow-right-s-line text-background-300 text-sm" />
              </Link>
            ))}
          </div>
        </div>

        <div className="absolute bottom-6 left-4 md:left-6 md:right-[360px] flex items-center justify-between">
          <div className="flex items-center gap-2">
            {heroAnime.map((anime, i) => (
              <button
                key={anime.id}
                type="button"
                onClick={() => go(i)}
                aria-label={`切换到${anime.title}`}
                className={`h-1.5 rounded-full transition-all cursor-pointer ${i === index ? "w-8 bg-accent-500" : "w-4 bg-background-50/40 hover:bg-background-50/70"}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => go(index - 1)}
              aria-label="上一张"
              className="w-9 h-9 rounded-full border border-background-100/40 text-background-50 flex items-center justify-center hover:bg-background-50/15 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <button
              type="button"
              onClick={() => go(index + 1)}
              aria-label="下一张"
              className="w-9 h-9 rounded-full border border-background-100/40 text-background-50 flex items-center justify-center hover:bg-background-50/15 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}